/**
 * Mock VAS/VIK exception data — 5 VIK commitment cases with timelines, entities, and signals.
 */
import {
  ExceptionCase,
  TimelineEvent,
  EntityNode,
  EntityEdge,
  Signal,
} from './mockExceptionData';

// ─── VIK Mock Cases ─────────────────────────────────────────────────────────

export const vikMockCases: ExceptionCase[] = [
  {
    case_id: 'VIK-2024-033',
    signal_id: 'sig-vik-033',
    domain: 'vas',
    workflow_type: 'vik_at_risk_exception',
    status: 'escalated',
    severity: 'critical',
    title: 'VIK-2024-033 commitment at risk — AeroConnect Airlines',
    description: '$4M VIK allocation at 60% drawn with 7 days remaining. No rollover allowed.',
    entity_type: 'vik_commitment',
    entity_id: 'VIK-2024-033',
    attributes: {
      allocation: 4_000_000,
      drawn: 2_400_000,
      remaining: 1_600_000,
      percent_delivered: 60,
      days_remaining: 7,
      commitment_type: 'vik',
      no_rollover: true,
    },
    assigned_team_id: 'vas_operations_team',
    created_at: '2026-05-24T09:00:00Z',
    updated_at: '2026-05-31T10:00:00Z',
  },
  {
    case_id: 'VIK-2024-041',
    signal_id: 'sig-vik-041',
    domain: 'vas',
    workflow_type: 'vik_at_risk_exception',
    status: 'investigating',
    severity: 'high',
    title: 'VIK-2024-041 commitment at risk — MetroBank Merchant Services',
    description: '$2.5M VIK allocation at 44% drawn with 14 days remaining. Drawdown rate declining.',
    entity_type: 'vik_commitment',
    entity_id: 'VIK-2024-041',
    attributes: {
      allocation: 2_500_000,
      drawn: 1_100_000,
      remaining: 1_400_000,
      percent_delivered: 44,
      days_remaining: 14,
      commitment_type: 'vik',
      no_rollover: false,
    },
    assigned_team_id: 'vas_operations_team',
    created_at: '2026-05-20T11:00:00Z',
    updated_at: '2026-05-31T09:15:00Z',
  },
  {
    case_id: 'VIK-2024-052',
    signal_id: 'sig-vik-052',
    domain: 'vas',
    workflow_type: 'vik_at_risk_exception',
    status: 'approval_needed',
    severity: 'high',
    title: 'VIK-2024-052 commitment at risk — ShopSphere Marketplace',
    description: '$1.8M VIK allocation at 67% drawn with 21 days remaining. Extension proposal pending.',
    entity_type: 'vik_commitment',
    entity_id: 'VIK-2024-052',
    attributes: {
      allocation: 1_800_000,
      drawn: 1_200_000,
      remaining: 600_000,
      percent_delivered: 67,
      days_remaining: 21,
      commitment_type: 'vik',
      no_rollover: true,
    },
    assigned_team_id: 'vas_operations_team',
    created_at: '2026-05-15T08:30:00Z',
    updated_at: '2026-05-30T14:00:00Z',
  },
  {
    case_id: 'VIK-2024-058',
    signal_id: 'sig-vik-058',
    domain: 'vas',
    workflow_type: 'vik_at_risk_exception',
    status: 'detected',
    severity: 'medium',
    title: 'VIK-2024-058 commitment at risk — NorthStar Retail Group',
    description: '$3.2M VIK allocation at 91% drawn with 5 days remaining. Close to threshold.',
    entity_type: 'vik_commitment',
    entity_id: 'VIK-2024-058',
    attributes: {
      allocation: 3_200_000,
      drawn: 2_900_000,
      remaining: 300_000,
      percent_delivered: 91,
      days_remaining: 5,
      commitment_type: 'vik',
      no_rollover: false,
    },
    assigned_team_id: 'vas_operations_team',
    created_at: '2026-05-28T07:00:00Z',
    updated_at: '2026-05-31T06:00:00Z',
  },
  {
    case_id: 'VIK-2024-061',
    signal_id: 'sig-vik-061',
    domain: 'vas',
    workflow_type: 'vik_at_risk_exception',
    status: 'action_taken',
    severity: 'critical',
    title: 'VIK-2024-061 commitment at risk — Horizon Travel Network',
    description: '$5M VIK allocation at 76% drawn with 29 days remaining. Escalation in progress.',
    entity_type: 'vik_commitment',
    entity_id: 'VIK-2024-061',
    attributes: {
      allocation: 5_000_000,
      drawn: 3_800_000,
      remaining: 1_200_000,
      percent_delivered: 76,
      days_remaining: 29,
      commitment_type: 'vik',
      no_rollover: true,
    },
    assigned_team_id: 'vas_operations_team',
    created_at: '2026-05-10T10:00:00Z',
    updated_at: '2026-05-31T11:30:00Z',
  },
];

// ─── VIK Mock Signals ────────────────────────────────────────────────────────

export const vikMockSignals: Signal[] = [
  {
    signal_id: 'sig-vik-033',
    source: 'scheduled_scan',
    domain: 'vas',
    workflow_type: 'vik_at_risk_exception',
    severity: 'critical',
    title: 'VIK commitment at risk — AeroConnect Airlines',
    description: '$4M VIK allocation at 60% drawn with 7 days remaining',
    entity_type: 'vik_commitment',
    entity_id: 'VIK-2024-033',
    attributes: { allocation: 4_000_000, percent_delivered: 60, days_remaining: 7 },
    detected_at: '2026-05-24T08:50:00Z',
  },
  {
    signal_id: 'sig-vik-041',
    source: 'scheduled_scan',
    domain: 'vas',
    workflow_type: 'vik_at_risk_exception',
    severity: 'high',
    title: 'VIK commitment at risk — MetroBank Merchant Services',
    description: '$2.5M VIK allocation at 44% drawn with 14 days remaining',
    entity_type: 'vik_commitment',
    entity_id: 'VIK-2024-041',
    attributes: { allocation: 2_500_000, percent_delivered: 44, days_remaining: 14 },
    detected_at: '2026-05-20T10:50:00Z',
  },
  {
    signal_id: 'sig-vik-052',
    source: 'rule_engine',
    domain: 'vas',
    workflow_type: 'vik_at_risk_exception',
    severity: 'high',
    title: 'VIK commitment at risk — ShopSphere Marketplace',
    description: '$1.8M VIK allocation at 67% drawn with 21 days remaining',
    entity_type: 'vik_commitment',
    entity_id: 'VIK-2024-052',
    attributes: { allocation: 1_800_000, percent_delivered: 67, days_remaining: 21 },
    detected_at: '2026-05-15T08:20:00Z',
  },
  {
    signal_id: 'sig-vik-058',
    source: 'scheduled_scan',
    domain: 'vas',
    workflow_type: 'vik_at_risk_exception',
    severity: 'medium',
    title: 'VIK commitment at risk — NorthStar Retail Group',
    description: '$3.2M VIK allocation at 91% drawn with 5 days remaining',
    entity_type: 'vik_commitment',
    entity_id: 'VIK-2024-058',
    attributes: { allocation: 3_200_000, percent_delivered: 91, days_remaining: 5 },
    detected_at: '2026-05-28T06:50:00Z',
  },
  {
    signal_id: 'sig-vik-061',
    source: 'webhook',
    domain: 'vas',
    workflow_type: 'vik_at_risk_exception',
    severity: 'critical',
    title: 'VIK commitment at risk — Horizon Travel Network',
    description: '$5M VIK allocation at 76% drawn with 29 days remaining',
    entity_type: 'vik_commitment',
    entity_id: 'VIK-2024-061',
    attributes: { allocation: 5_000_000, percent_delivered: 76, days_remaining: 29 },
    detected_at: '2026-05-10T09:50:00Z',
  },
];

// ─── VIK Mock Timeline ──────────────────────────────────────────────────────

export const vikMockTimeline: Record<string, TimelineEvent[]> = {
  'VIK-2024-033': [
    { type: 'case_created', timestamp: '2026-05-24T09:00:00Z', data: { status: 'detected' } },
    {
      type: 'agent_trace',
      timestamp: '2026-05-24T09:01:00Z',
      data: {
        agent_name: 'VAS Commitment Orchestrator',
        operation: 'plan',
        output_summary: 'Opened case VIK-2024-033 for AeroConnect Airlines. Assigned investigation: pull drawdown status, extract contract terms, forecast utilization, search precedents, propose action.',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-05-24T09:03:00Z',
      data: {
        agent_name: 'VIK Drawdown Analyst',
        operation: 'get_drawdown_status',
        output_summary: 'VIK-2024-033: $4M allocation, $2.4M drawn (60%), $1.6M remaining. Monthly avg drawdown $400K. 7 days until expiry.',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-05-24T09:06:00Z',
      data: {
        agent_name: 'Contract Intelligence Agent',
        operation: 'extract_contract_terms',
        output_summary: 'Contract AER-VIK-2024: No-rollover clause (Section C.4). Fixed VAS $4M/year. Forfeiture language on unallocated amounts at program year-end.',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-05-24T09:09:00Z',
      data: {
        agent_name: 'Utilization Forecast Agent',
        operation: 'forecast_utilization',
        output_summary: 'Projected utilization: 70% ($2.8M of $4M) at current drawdown rate. Forfeiture risk: $1.2M (30%). Probability of full utilization without intervention: 8%.',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-05-24T09:12:00Z',
      data: {
        agent_name: 'Precedent Search Agent',
        operation: 'search_precedents',
        output_summary: 'Found 3 similar airline VIK cases: (1) SkyWest 2023 — extended 30 days, fulfilled 95%. (2) JetBlue 2024 — escalated, fulfilled 100%. (3) Alaska Air 2023 — extended 45 days, fulfilled 98%. Intervention success rate: 97%.',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-05-24T09:15:00Z',
      data: {
        agent_name: 'Partner Ops Action Agent',
        operation: 'propose_action',
        output_summary: 'Recommended action: 30-day extension for AeroConnect Airlines. Created escalation packet with drawdown analysis, contract context, and precedent data. Drafted partner outreach communication.',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-05-24T09:18:00Z',
      data: {
        agent_name: 'Policy & Approval Agent',
        operation: 'route_approval',
        output_summary: 'Validated extension request against VAS policy framework. Extension within authority limits. Routed approval request to Daniel Kim (VP Partner Operations) with full evidence package.',
      },
    },
    {
      type: 'evidence_collected',
      timestamp: '2026-05-24T09:19:00Z',
      data: {
        title: 'Contract terms — no rollover clause',
        evidence_type: 'document',
        content: 'Section C.4: Unallocated VAS amounts are forfeited at program year-end. No rollover. Fixed allocation of $4M/year.',
      },
    },
    {
      type: 'evidence_collected',
      timestamp: '2026-05-24T09:20:00Z',
      data: {
        title: 'Precedent analysis — airline VIK cases',
        evidence_type: 'precedent',
        content: '3/3 airline partners with similar drawdown profiles fulfilled after intervention. Average fulfillment after extension: 97.7%. Zero cases fulfilled without intervention at this drawdown rate.',
      },
    },
    {
      type: 'action',
      timestamp: '2026-05-24T09:22:00Z',
      data: {
        action_type: 'escalate_to_partner_ops',
        description: 'Escalate to partner operations for 30-day extension. Precedent strongly supports intervention (3/3 airline cases fulfilled post-extension).',
        status: 'executed',
      },
    },
    {
      type: 'hitl_request',
      timestamp: '2026-05-24T09:25:00Z',
      data: {
        title: 'Approve 30-day extension for VIK-2024-033 (AeroConnect Airlines)',
        status: 'pending',
        assignee: 'Daniel Kim',
        options: ['approve', 'reject', 'escalate'],
      },
    },
    {
      type: 'decision',
      timestamp: '2026-05-26T10:00:00Z',
      data: {
        action: 'approve',
        decision_source: 'human',
        reasoning: 'Precedent data compelling. AeroConnect confirmed accelerated delivery schedule. Approve 30-day extension.',
        owner: 'Daniel Kim',
      },
    },
    {
      type: 'outcome',
      timestamp: '2026-05-30T16:00:00Z',
      data: {
        final_status: 'resolved',
        resolution_summary: 'VIK commitment fulfilled after 30-day extension. AeroConnect Airlines completed all deliverables. $4M fully utilized.',
        total_duration_hours: 151,
        human_touches: 1,
      },
    },
  ],
  'VIK-2024-041': [
    { type: 'case_created', timestamp: '2026-05-20T11:00:00Z', data: { status: 'detected' } },
    {
      type: 'agent_trace',
      timestamp: '2026-05-20T11:02:00Z',
      data: {
        agent_name: 'VAS Commitment Orchestrator',
        operation: 'plan',
        output_summary: 'Opened case VIK-2024-041 for MetroBank Merchant Services. Assigned drawdown analysis and contract review.',
      },
    },
  ],
  'VIK-2024-052': [
    { type: 'case_created', timestamp: '2026-05-15T08:30:00Z', data: { status: 'detected' } },
    {
      type: 'agent_trace',
      timestamp: '2026-05-15T08:32:00Z',
      data: {
        agent_name: 'VAS Commitment Orchestrator',
        operation: 'plan',
        output_summary: 'Opened case VIK-2024-052 for ShopSphere Marketplace. Extension proposal being prepared.',
      },
    },
  ],
  'VIK-2024-058': [
    { type: 'case_created', timestamp: '2026-05-28T07:00:00Z', data: { status: 'detected' } },
  ],
  'VIK-2024-061': [
    { type: 'case_created', timestamp: '2026-05-10T10:00:00Z', data: { status: 'detected' } },
    {
      type: 'agent_trace',
      timestamp: '2026-05-10T10:02:00Z',
      data: {
        agent_name: 'VAS Commitment Orchestrator',
        operation: 'plan',
        output_summary: 'Opened case VIK-2024-061 for Horizon Travel Network. $5M allocation, escalation workflow initiated.',
      },
    },
  ],
};

// ─── VIK Mock Entities ──────────────────────────────────────────────────────

export const vikMockEntities: Record<string, { nodes: EntityNode[]; edges: EntityEdge[] }> = {
  'VIK-2024-033': {
    nodes: [
      { entity_id: 'VIK-2024-033', entity_type: 'vik_commitment', display_name: 'VIK-2024-033 ($4M allocation)' },
      { entity_id: 'PARTNER-AEROCONNECT', entity_type: 'partner', display_name: 'AeroConnect Airlines' },
      { entity_id: 'CONTRACT-AER-VIK-2024', entity_type: 'contract', display_name: 'Contract AER-VIK-2024' },
      { entity_id: 'AE-DKIM', entity_type: 'account_exec', display_name: 'Daniel Kim (VP Partner Ops)' },
      { entity_id: 'POLICY-VIK-NOROLL-033', entity_type: 'policy', display_name: 'No-Rollover Policy (Section C.4)' },
      { entity_id: 'DRAWDOWN-HIST-033', entity_type: 'drawdown_history', display_name: 'Drawdown History ($2.4M / $4M)' },
      { entity_id: 'PRECEDENT-SKYWEST23', entity_type: 'precedent', display_name: 'SkyWest 2023 (extended, fulfilled 95%)' },
      { entity_id: 'PRECEDENT-JETBLUE24', entity_type: 'precedent', display_name: 'JetBlue 2024 (escalated, fulfilled 100%)' },
      { entity_id: 'DECISION-EXTEND-033', entity_type: 'decision', display_name: 'Decision: 30-day extension approved' },
    ],
    edges: [
      { source: 'VIK-2024-033', target: 'PARTNER-AEROCONNECT', relationship: 'committed_to' },
      { source: 'VIK-2024-033', target: 'CONTRACT-AER-VIK-2024', relationship: 'governed_by' },
      { source: 'CONTRACT-AER-VIK-2024', target: 'POLICY-VIK-NOROLL-033', relationship: 'contains_clause' },
      { source: 'PARTNER-AEROCONNECT', target: 'AE-DKIM', relationship: 'managed_by' },
      { source: 'VIK-2024-033', target: 'DRAWDOWN-HIST-033', relationship: 'has_drawdown' },
      { source: 'DECISION-EXTEND-033', target: 'VIK-2024-033', relationship: 'decided_on' },
      { source: 'DECISION-EXTEND-033', target: 'PRECEDENT-SKYWEST23', relationship: 'informed_by' },
      { source: 'DECISION-EXTEND-033', target: 'PRECEDENT-JETBLUE24', relationship: 'informed_by' },
      { source: 'DECISION-EXTEND-033', target: 'POLICY-VIK-NOROLL-033', relationship: 'considered_policy' },
    ],
  },
  'VIK-2024-041': {
    nodes: [
      { entity_id: 'VIK-2024-041', entity_type: 'vik_commitment', display_name: 'VIK-2024-041 ($2.5M allocation)' },
      { entity_id: 'PARTNER-METROBANK', entity_type: 'partner', display_name: 'MetroBank Merchant Services' },
      { entity_id: 'CONTRACT-MBK-VIK-2024', entity_type: 'contract', display_name: 'Contract MBK-VIK-2024' },
    ],
    edges: [
      { source: 'VIK-2024-041', target: 'PARTNER-METROBANK', relationship: 'committed_to' },
      { source: 'VIK-2024-041', target: 'CONTRACT-MBK-VIK-2024', relationship: 'governed_by' },
    ],
  },
  'VIK-2024-052': {
    nodes: [
      { entity_id: 'VIK-2024-052', entity_type: 'vik_commitment', display_name: 'VIK-2024-052 ($1.8M allocation)' },
      { entity_id: 'PARTNER-SHOPSPHERE', entity_type: 'partner', display_name: 'ShopSphere Marketplace' },
      { entity_id: 'CONTRACT-SS-VIK-2024', entity_type: 'contract', display_name: 'Contract SS-VIK-2024' },
      { entity_id: 'POLICY-VIK-NOROLL-052', entity_type: 'policy', display_name: 'No-Rollover Policy' },
    ],
    edges: [
      { source: 'VIK-2024-052', target: 'PARTNER-SHOPSPHERE', relationship: 'committed_to' },
      { source: 'VIK-2024-052', target: 'CONTRACT-SS-VIK-2024', relationship: 'governed_by' },
      { source: 'CONTRACT-SS-VIK-2024', target: 'POLICY-VIK-NOROLL-052', relationship: 'contains_clause' },
    ],
  },
  'VIK-2024-058': {
    nodes: [
      { entity_id: 'VIK-2024-058', entity_type: 'vik_commitment', display_name: 'VIK-2024-058 ($3.2M allocation)' },
      { entity_id: 'PARTNER-NORTHSTAR', entity_type: 'partner', display_name: 'NorthStar Retail Group' },
    ],
    edges: [
      { source: 'VIK-2024-058', target: 'PARTNER-NORTHSTAR', relationship: 'committed_to' },
    ],
  },
  'VIK-2024-061': {
    nodes: [
      { entity_id: 'VIK-2024-061', entity_type: 'vik_commitment', display_name: 'VIK-2024-061 ($5M allocation)' },
      { entity_id: 'PARTNER-HORIZON', entity_type: 'partner', display_name: 'Horizon Travel Network' },
      { entity_id: 'CONTRACT-HTN-VIK-2024', entity_type: 'contract', display_name: 'Contract HTN-VIK-2024' },
      { entity_id: 'POLICY-VIK-NOROLL-061', entity_type: 'policy', display_name: 'No-Rollover Policy' },
    ],
    edges: [
      { source: 'VIK-2024-061', target: 'PARTNER-HORIZON', relationship: 'committed_to' },
      { source: 'VIK-2024-061', target: 'CONTRACT-HTN-VIK-2024', relationship: 'governed_by' },
      { source: 'CONTRACT-HTN-VIK-2024', target: 'POLICY-VIK-NOROLL-061', relationship: 'contains_clause' },
    ],
  },
};
